import React, { useEffect, useMemo, useState } from 'react';
import appointmentService from '../../services/appointmentService';

const DAY_PARTS = [
  { key: 'morning', label: 'Morning', test: (h) => h < 12 },
  { key: 'afternoon', label: 'Afternoon', test: (h) => h >= 12 && h < 17 },
  { key: 'evening', label: 'Evening', test: (h) => h >= 17 },
];

/** Plain-language reason for a failed request, so the user knows whether to retry or to call someone. */
export const describeSlotError = (err) => {
  const serverMessage = err?.response?.data?.message;
  if (serverMessage) return serverMessage;
  if (err?.response?.status === 404) {
    return 'The appointment service is not available on the server yet. Ask your administrator to update and restart the LIS backend.';
  }
  if (!err?.response) return 'The server could not be reached. Check the connection and try again.';
  return 'Could not load the slots.';
};

/**
 * Time-slot grid for one centre and date. Availability comes from the server (capacity, closed days, past slots);
 * the server re-checks everything on save, so a slot taken in the meantime is reported then.
 */
export const SlotPicker = ({ centreId, departmentId, doctorId = null, date, value, onChange, disabledSlot = null, refreshKey = 0, asSelect = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    if (!date) {
      setData(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    appointmentService
      // without a chosen doctor the call is exactly what it always was
      .slots(...(doctorId ? [date, centreId, departmentId, doctorId] : [date, centreId, departmentId]))
      .then((res) => {
        if (!cancelled) setData(res.success ? res.data : null);
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          setError(describeSlotError(err));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [date, centreId, departmentId, doctorId, refreshKey, reload]);

  // Slots that already started are hidden until asked for, but only when something can still be booked (otherwise the whole day stays visible).
  const pastCount = (data?.slots || []).filter((s) => s.isPast).length;
  const hidePast = !showPast && pastCount > 0 && (data?.slots || []).some((s) => s.isBookable);

  const groups = useMemo(() => {
    const slots = (data?.slots || []).filter((s) => !(hidePast && s.isPast));
    return DAY_PARTS
      .map((p) => ({ ...p, slots: slots.filter((s) => p.test(parseInt(s.slotStart.slice(0, 2), 10))) }))
      .filter((g) => g.slots.length > 0);
  }, [data, hidePast]);

  if (!date) return <p className="apt-hint">Choose a date to see the available slots.</p>;
  if (loading) {
    return (
      <div className="apt-slot-grid" aria-busy="true" aria-label="Loading slots">
        {Array.from({ length: 8 }).map((_, i) => <span key={i} className="apt-skeleton apt-skeleton-slot" />)}
      </div>
    );
  }
  if (error) {
    return (
      <div className="apt-state apt-state-error" role="alert">
        <p>{error}</p>
        <button type="button" className="apt-btn apt-btn-sm" onClick={() => setReload((n) => n + 1)}>Try again</button>
      </div>
    );
  }
  if (!data) return null;
  if (data.isClosed) return <div className="apt-state apt-state-closed"><p>{data.closedReason || 'No slots on this date.'}</p></div>;
  if (!data.slots?.length) return <p className="apt-hint">No slots are configured for this date.</p>;

  // Compact form: one dropdown with the slots that have not started yet, grouped by part of the day.
  if (asSelect) {
    const open = data.slots.filter((s) => !s.isPast);
    return (
      <div className="apt-slots">
        <select className="apt-input" aria-label="Time slot" value={value || ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select a time slot…</option>
          {DAY_PARTS.map((p) => ({ ...p, slots: open.filter((s) => p.test(parseInt(s.slotStart.slice(0, 2), 10))) })).filter((g) => g.slots.length > 0).map((g) => (
            <optgroup key={g.key} label={g.label}>
              {g.slots.map((s) => (
                <option key={s.slotStart} value={s.slotStart} disabled={!s.isBookable || disabledSlot === s.slotStart}>
                  {s.slotStart} · {disabledSlot === s.slotStart ? 'current' : s.available === 0 ? 'full' : `${s.available} left`}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        {!data.slots.some((s) => s.isBookable) && (
          <p className="apt-hint">No slot can be booked on this date (all are full or already past). Choose another day.</p>
        )}
      </div>
    );
  }

  return (
    <div className="apt-slots">
      {groups.map((g) => (
        <div key={g.key} className="apt-slot-group">
          {groups.length > 1 && <div className="apt-slot-group-title">{g.label}</div>}
          <div className="apt-slot-grid" role="listbox" aria-label={`${g.label} time slots`}>
            {g.slots.map((s) => {
              const isCurrent = disabledSlot === s.slotStart;
              const unavailable = !s.isBookable || isCurrent;
              const selected = value === s.slotStart;
              return (
                <button
                  key={s.slotStart}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  disabled={unavailable}
                  className={`apt-slot ${selected ? 'selected' : ''} ${unavailable ? 'unavailable' : ''}`}
                  onClick={() => onChange(s.slotStart)}
                  title={isCurrent ? 'Current slot' : s.isPast ? 'Already started' : s.available === 0 ? 'Full' : `${s.available} of ${s.capacity} places left`}
                >
                  <span className="apt-slot-time">{s.slotStart}</span>
                  <span className="apt-slot-left">
                    {isCurrent ? 'current' : s.isPast ? 'past' : s.available === 0 ? 'full' : `${s.available} left`}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {!data.slots.some((s) => s.isBookable) && (
        <p className="apt-hint">No slot can be booked on this date (all are full or already past). Choose another day.</p>
      )}
      {pastCount > 0 && (hidePast || showPast) && (
        <button type="button" className="apt-link apt-slot-past-toggle" onClick={() => setShowPast((v) => !v)}>
          {hidePast ? `Show ${pastCount} past slot${pastCount === 1 ? '' : 's'}` : 'Hide past slots'}
        </button>
      )}
      <div className="apt-slot-legend" aria-hidden="true">
        <span><i className="apt-dot apt-dot-free" /> Available</span>
        <span><i className="apt-dot apt-dot-selected" /> Selected</span>
        <span><i className="apt-dot apt-dot-off" /> Full or past</span>
      </div>
    </div>
  );
};

export default SlotPicker;
